/**
 * Ops route tree, composable under any parent path.
 *
 * Shell mounts it at `/ops/*`; standalone mounts at the repo root. The
 * `opsRoutes(basePath)` factory prefixes sidebar `<Link>`s with the mount
 * path. Route-object paths stay relative so React Router resolves them
 * against the mount point in either mode.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Outlet, Navigate, useLocation, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { AppShell, type NavEntry } from '@widgets/app-shell';
const ModuleGuidePage = lazy(() =>
  import('@widgets/module-guide').then((m) => ({ default: m.ModuleGuidePage }))
);
import { ErrorBoundary } from '@shared/ui';
import {
  ADMIN_FEE_APPROVER_ROLE, ADMIN_FEE_EDITOR_ROLE, ProtectedRoute, can, canManageCrmMatches,
} from '@shared/auth';
import { useAuth } from '@shared/providers/AuthProvider';
import { canProvisionSites } from '@entities/groupdocs-sites';
import { QuickNoteFab } from '@widgets/notes';
import { useOpsGroup } from '@entities/ops-group';
import { useRecordGroupBackTarget } from '@ops/shared/groupBackTarget';
import { HeaderGroupSearch } from '../HeaderGroupSearch';
import { useOpsNavCounts, type OpsNavCounts } from './useOpsNavCounts';
import {
  BoardRegular,
  ClipboardTaskListLtrRegular,
  PeopleRegular,
  ArrowSyncRegular,
  WrenchScrewdriverRegular,
  StarRegular,
  SearchRegular,
  ShieldErrorRegular,
  WeatherSunnyRegular,
  FormNewRegular,
  ListRegular,
  TaskListSquareLtrRegular,
  BroomRegular,
  CheckmarkCircleRegular,
  CloudRegular,
  GlobeRegular,
} from '@fluentui/react-icons';

const DashboardPage = lazy(() => import('@ops/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const IntakePage = lazy(() => import('@ops/pages/intake').then(m => ({ default: m.IntakePage })));
const GroupsPage = lazy(() => import('@ops/pages/groups').then(m => ({ default: m.GroupsPage })));
const GroupDetailV2Page = lazy(() => import('@ops/pages/group-detail').then(m => ({ default: m.GroupDetailV2Page })));
const FaceSheetPage = lazy(() => import('@ops/pages/face-sheet').then(m => ({ default: m.FaceSheetPage })));
const PlanPacketPage = lazy(() => import('@ops/pages/plan-packet').then(m => ({ default: m.PlanPacketPage })));
const ManagedListsPage = lazy(() => import('@ops/pages/managed-lists').then(m => ({ default: m.ManagedListsPage })));
const RenewalsPage = lazy(() => import('@ops/pages/renewals').then(m => ({ default: m.RenewalsPage })));
const ImplementationOverviewPage = lazy(() => import('@ops/pages/implementation').then(m => ({ default: m.ImplementationOverviewPage })));
const ImpGanttPage = lazy(() => import('@ops/pages/implementation').then(m => ({ default: m.ImpGanttPage })));
const MyGroupsPage = lazy(() => import('@ops/pages/my-groups').then(m => ({ default: m.MyGroupsPage })));
const OperationDaylightPage = lazy(() => import('@ops/pages/operation-daylight').then(m => ({ default: m.OperationDaylightPage })));
const DaylightTeamPage = lazy(() => import('@ops/pages/operation-daylight').then(m => ({ default: m.DaylightTeamPage })));
const SearchPage = lazy(() => import('@ops/pages/search').then(m => ({ default: m.SearchPage })));
const GroupsMapPage = lazy(() => import('@ops/pages/groups-map').then(m => ({ default: m.GroupsMapPage })));
const CrmMatchesPage = lazy(() => import('@ops/pages/crm-matches').then(m => ({ default: m.CrmMatchesPage })));
const ImpFormsPage = lazy(() => import('@ops/pages/impforms').then(m => ({ default: m.ImpFormsPage })));
const ImpFormReviewPage = lazy(() => import('@ops/pages/impforms').then(m => ({ default: m.ImpFormReviewPage })));
const MyTasksPage = lazy(() => import('@ops/pages/my-tasks').then(m => ({ default: m.MyTasksPage })));
const DataOpportunitiesPage = lazy(() => import('@ops/pages/data-opportunities').then(m => ({ default: m.DataOpportunitiesPage })));
const GroupSiteProvisioningPage = lazy(() => import('@ops/pages/group-site-provisioning').then(m => ({ default: m.GroupSiteProvisioningPage })));
const ApprovalsPage = lazy(() => import('@ops/pages/approvals').then(m => ({ default: m.ApprovalsPage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const OpsBasePathContext = createContext<string>('');

function buildNavItems(
  basePath: string,
  showHousekeeping: boolean,
  canProvision: boolean,
  canApprove: boolean,
  counts: OpsNavCounts = {}
): NavEntry[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  // At-a-glance workload counts (ADO #1597). A zero or still-loading count
  // renders no badge at all.
  const badge = (n?: number) => (n ? String(n) : undefined);
  const items: NavEntry[] = [
    { path: p('/'), label: 'Dashboard', icon: BoardRegular },
    { path: p('/operation-daylight'), label: 'Operation Daylight', icon: WeatherSunnyRegular },
    // Personal surfaces — this user's own book and inbox.
    { kind: 'section', label: 'My Work' },
    { path: p('/my-groups'), label: 'My Groups', icon: StarRegular },
    { path: p('/my-tasks'), label: 'My Tasks', icon: TaskListSquareLtrRegular },
    { path: p('/groups'), label: 'All Groups', icon: PeopleRegular },
    { path: p('/search'), label: 'Advanced Search', icon: SearchRegular },
    { path: p('/groups-map'), label: 'Groups Map', icon: GlobeRegular },
    // Lifecycle workflows — a group moving into, through, or out of the book.
    { kind: 'section', label: 'Groups in Transition' },
    { path: p('/intake'), label: 'Intake', icon: ClipboardTaskListLtrRegular, badge: badge(counts.intake) },
    { path: p('/transitions'), label: 'Transitions', icon: WrenchScrewdriverRegular, badge: badge(counts.transitions) },
    { path: p('/impforms'), label: 'Implementation Forms', icon: FormNewRegular },
    { path: p('/renewals'), label: 'Renewals', icon: ArrowSyncRegular, badge: badge(counts.renewals) },
  ];
  // The admin-fee approval queue (9/04): AdminFee.Approver only. The ADO
  // #1250 Manager view can embed this later; for now it is its own page.
  if (canApprove) {
    items.push({
      path: p('/approvals'), label: 'Approvals', icon: CheckmarkCircleRegular,
      badge: badge(counts.approvals),
    });
  }
  // Data-repair + list-management tools — gated to Ops.DataAdmin (or global
  // Admin); Group Site Provisioning is gated separately to Site Provisioners.
  if (showHousekeeping || canProvision) {
    items.push({ kind: 'section', label: 'Housekeeping' });
    if (showHousekeeping) {
      items.push(
        { path: p('/data-opportunities'), label: 'Data Opportunities', icon: BroomRegular },
        { path: p('/crm-matches'), label: 'CRM Matches', icon: ShieldErrorRegular },
        { path: p('/managed-lists'), label: 'Managed Lists', icon: ListRegular },
      );
    }
    if (canProvision) {
      items.push({ path: p('/group-site-provisioning'), label: 'Group Site Provisioning', icon: CloudRegular });
    }
  }
  return items;
}

/**
 * Match `/groups/<id>` anywhere in the pathname so the FAB works both at
 * `/groups/<id>` (standalone) and `/ops/groups/<id>` (shell).
 */
function ContextualQuickNoteFab() {
  const location = useLocation();
  const match = location.pathname.match(/\/groups\/([^/]+)/);
  const groupId = match?.[1];
  const { data: opsGroup } = useOpsGroup(groupId ?? '');
  return (
    <QuickNoteFab
      defaultAccountId={opsGroup?.accountId}
      defaultAccountName={opsGroup?.name}
    />
  );
}

export function OpsRootLayout() {
  const basePath = useContext(OpsBasePathContext);
  // Remembers the page a group was opened from, so the detail page's back
  // arrow returns there instead of dumping everyone on All Groups (#1625).
  useRecordGroupBackTarget(basePath);
  const { roles } = useAuth();
  const navCounts = useOpsNavCounts();
  const navItems = buildNavItems(
    basePath,
    canManageCrmMatches(roles),
    canProvisionSites(roles ?? []),
    can('adminfee.approve', roles ?? []),
    navCounts
  );
  return (
    // The AdminFee pair opens the Ops surface too: an editor's authoring tab
    // and the approver's queue both live here, and neither role implies Ops.*.
    <ProtectedRoute appRolePrefix="Ops" extraRoles={[ADMIN_FEE_EDITOR_ROLE, ADMIN_FEE_APPROVER_ROLE]}>
      <ErrorBoundary>
        <AppShell
          appId="ops"
          appTitle="Vera Operations"
          navItems={navItems}
          headerSearch={<HeaderGroupSearch />}
        >
          <Outlet />
          <ContextualQuickNoteFab />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Ops route tree, with sidebar links prefixed for the mount
 * path. Standalone: `opsRoutes()` at repo root. Shell: `opsRoutes('/ops')`.
 */
export function opsRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <OpsBasePathContext.Provider value={basePath}>
          <OpsRootLayout />
        </OpsBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><DashboardPage /></LazyPage> },
        { path: 'intake', element: <LazyPage><IntakePage /></LazyPage> },
        { path: 'my-groups', element: <LazyPage><MyGroupsPage /></LazyPage> },
        { path: 'my-tasks', element: <LazyPage><MyTasksPage /></LazyPage> },
        { path: 'operation-daylight', element: <LazyPage><OperationDaylightPage /></LazyPage> },
        { path: 'operation-daylight/team/:aeOwnerId', element: <LazyPage><DaylightTeamPage /></LazyPage> },
        { path: 'groups', element: <LazyPage><GroupsPage /></LazyPage> },
        { path: 'search', element: <LazyPage><SearchPage /></LazyPage> },
        { path: 'groups-map', element: <LazyPage><GroupsMapPage /></LazyPage> },
        { path: 'groups/:groupId', element: <LazyPage><GroupDetailV2Page /></LazyPage> },
        { path: 'groups/:groupId/face-sheet', element: <LazyPage><FaceSheetPage /></LazyPage> },
        { path: 'groups/:groupId/plan-packet', element: <LazyPage><PlanPacketPage /></LazyPage> },
        // Groups in Transition dashboard (chunk 7, renamed from Implementations
        // 2026-07-15). Old /implementations links redirect here; the gantt stays
        // mounted at both paths so existing bookmarks keep resolving.
        { path: 'transitions', element: <LazyPage><ImplementationOverviewPage /></LazyPage> },
        { path: 'transitions/:projectId/gantt', element: <LazyPage><ImpGanttPage /></LazyPage> },
        { path: 'implementations', element: <Navigate to={`${basePath}/transitions`} replace /> },
        { path: 'implementations/:projectId/gantt', element: <LazyPage><ImpGanttPage /></LazyPage> },
        { path: 'renewals', element: <LazyPage><RenewalsPage /></LazyPage> },
        { path: 'approvals', element: <LazyPage><ApprovalsPage /></LazyPage> },
        { path: 'impforms', element: <LazyPage><ImpFormsPage /></LazyPage> },
        { path: 'impforms/:instanceId/review', element: <LazyPage><ImpFormReviewPage /></LazyPage> },
        { path: 'managed-lists', element: <LazyPage><ManagedListsPage /></LazyPage> },
        { path: 'crm-matches', element: <LazyPage><CrmMatchesPage /></LazyPage> },
        { path: 'data-opportunities', element: <LazyPage><DataOpportunitiesPage /></LazyPage> },
        { path: 'group-site-provisioning', element: <LazyPage><GroupSiteProvisioningPage /></LazyPage> },
        // Guide retired into Academy Know (module filter preserved).
        // The module's own documentation, read in the module's own chrome. This
        // replaced a redirect to /academy/know, which sent people out of the
        // module to read about it (Sasha, 2026-08-16).
        { path: 'guide', element: <LazyPage><ModuleGuidePage module="ops" basePath={basePath} /></LazyPage> },
        { path: 'guide/:slug', element: <LazyPage><ModuleGuidePage module="ops" basePath={basePath} /></LazyPage> },
      ],
    },
  ];
}

